import { Sprite } from "pixi.js"
import { AnimationPoseView, JumpMount } from "game-common/animation_pose"
import { JUMP_MS, MOUNT_WAIT_MS, jumpLift, mountOffset } from "game-common/jump"

/** Where a flourish has the body at one moment: lifted off its soles, stretched, turned. */
export interface FlourishFrame {
    /** Body-frame pixels above the ground. */
    lift: number
    scaleX: number
    scaleY: number
    /** Radians, about the soles. */
    rotation: number
    /** Body-frame pixels across (a jump up onto a wall lands to one side of where it took off). */
    dx?: number
}

const smooth = (x: number) => {
    const t = Math.max(0, Math.min(1, x))
    return t * t * (3 - 2 * t)
}
const still: FlourishFrame = { lift: 0, scaleX: 1, scaleY: 1, rotation: 0 }


/**
 * The motions, over progress 0..1 (the server's FLOURISH_DURATION_MS sets how long). Each starts and
 * ends at rest, so a flourish never leaves the body somewhere it was not.
 */
export const FLOURISH_MOTIONS: Record<string, (t: number) => FlourishFrame> = {
    // A rabbit's binky: a crouch, a spring, a twist in the air -- the body turns all the way round
    // and back -- and a squashed landing.
    binky: t => {
        if (t < 0.14) {
            const u = smooth(t / 0.14)
            return { lift: 0, scaleX: 1 + 0.08 * u, scaleY: 1 - 0.12 * u, rotation: 0 }
        }
        if (t < 0.8) {
            const u = (t - 0.14) / 0.66
            const takeoff = Math.max(0, 1 - u * 3)
            const twist = Math.cos(2 * Math.PI * smooth((u - 0.12) / 0.72))
            return {
                lift: 15 * Math.sin(Math.PI * u),
                scaleX: (1 - 0.06 * takeoff) * twist,
                scaleY: 1 + 0.1 * takeoff,
                rotation: 0.16 * Math.sin(2 * Math.PI * u),
            }
        }
        const u = (t - 0.8) / 0.2
        const squash = Math.sin(Math.PI * u) * (1 - 0.3 * u)
        return { lift: 0, scaleX: 1 + 0.12 * squash, scaleY: 1 - 0.14 * squash, rotation: 0 }
    },
    // Jumping out of its skin: straight up, stretched tall, and down into a squash.
    startle: t => {
        if (t < 0.55) {
            const air = Math.sin(Math.PI * t / 0.55)
            return { lift: 8 * air, scaleX: 1 - 0.08 * air, scaleY: 1 + 0.12 * air, rotation: 0 }
        }
        const u = (t - 0.55) / 0.45
        const squash = Math.sin(Math.PI * u) * (1 - 0.4 * u)
        return { lift: 0, scaleX: 1 + 0.1 * squash, scaleY: 1 - 0.12 * squash, rotation: 0 }
    },
    // A player's jump (game-common/jump.ts): off the ground almost as the key goes down, up and down
    // on gravity's curve (a parabola, not a sine: a sine hangs at the top), and a moment on the
    // ground at the end. No squash or stretch -- scaled pixel art reads as warping; the body's own
    // crouch is its clip's.
    jump: t => ({ lift: jumpLift(t * JUMP_MS), scaleX: 1, scaleY: 1, rotation: 0 }),
}

export const flourishFrame = (action: string, progress: number): FlourishFrame => {
    const motion = FLOURISH_MOTIONS[action]
    return motion && progress >= 0 && progress < 1 ? motion(progress) : still
}

/**
 * A jump up onto a wall (AnimationPoseView.mount), `elapsedMs` in, for a body drawn in room `roomId`: up
 * and over onto the top from where it took off, and held there. Once the body has been moved up onto the
 * storey above, it is drawn from where it landed: what is left of the arc ends there.
 */
export const mountFrame = (mount: JumpMount, elapsedMs: number, roomId: string | undefined): FlourishFrame => {
    const offset = mountOffset(elapsedMs, mount.dx, mount.dy)
    const arrived = roomId !== undefined && roomId !== mount.roomId
    const x = arrived ? offset.x - mount.dx : offset.x, y = arrived ? offset.y - mount.dy : offset.y
    return { lift: -y, dx: x, scaleX: 1, scaleY: 1, rotation: 0 }
}

/**
 * Plays the flourishes the server sends (an `animationPose` with `flourish` set) on a body's drawn
 * frame: render-only, about the soles, never touching the entity's coordinates. The shadow reads the
 * frame's transform, so it stays on the ground while the body leaves it.
 */
export class BodyFlourish {
    private target?: Sprite
    private base?: { x: number; y: number; scaleX: number; scaleY: number; rotation: number }
    // The last few ids seen: a jumper's own sprite is handed its predicted jump and the server's echo
    // of it (one id), and may be handed an older one between them, which must not play again.
    private seenIds: string[] = []
    private playing?: { action: string; startedAt: number; durationMs: number; mount?: JumpMount }

    /** Pick up a flourish the server sent. Each one plays once, however often it is re-sent. */
    observe(view: AnimationPoseView | undefined, now: number): void {
        if (!view?.flourish || this.seenIds.includes(view.id)) return
        this.seenIds.push(view.id)
        if (this.seenIds.length > 4) this.seenIds.shift()
        if (!FLOURISH_MOTIONS[view.action] || !(view.durationMs > 0) || view.remainingMs <= 0) return
        this.playing = { action: view.action, startedAt: now - (view.durationMs - view.remainingMs), durationMs: view.durationMs,
            mount: view.mount }
    }

    get active(): boolean {
        return !!this.playing
    }

    /** How far through `action` the body is (0..1), while that is the flourish it is playing. */
    progressOf(action: string, now: number): number | undefined {
        const playing = this.playing
        if (playing?.action !== action) return undefined
        const progress = (now - playing.startedAt) / playing.durationMs
        return progress >= 0 && progress < 1 ? progress : undefined
    }

    /** Move `target` (the body's current frame) to where the flourish has it now; the body is in room `roomId`. */
    update(target: Sprite | undefined, ground: { x: number; y: number }, now: number, roomId?: string): void {
        if (target !== this.target) {
            this.restore()
            this.target = target
            this.base = undefined
            if (target) this.base = { x: target.x, y: target.y, scaleX: target.scale.x, scaleY: target.scale.y, rotation: target.rotation }
        }
        const playing = this.playing
        if (playing && now - playing.startedAt >= playing.durationMs) {
            // A jump up onto a wall stays drawn on the top until the body has been moved up there.
            const waiting = !!playing.mount && (roomId === undefined || roomId === playing.mount.roomId) &&
                now - playing.startedAt < playing.durationMs + MOUNT_WAIT_MS
            if (!waiting) this.playing = undefined
        }
        if (!this.target || !this.base) return
        if (!this.playing) {
            this.restore()
            return
        }
        const frame = playing.mount ? mountFrame(playing.mount, now - playing.startedAt, roomId)
            : flourishFrame(playing.action, (now - playing.startedAt) / playing.durationMs)
        const { x, y, scaleX, scaleY, rotation } = this.base
        // Scale and turn about the soles, then lift -- in whole body pixels, so the art stays crisp.
        const dx = (x - ground.x) * frame.scaleX, dy = (y - ground.y) * frame.scaleY
        const cos = Math.cos(frame.rotation), sin = Math.sin(frame.rotation)
        this.target.scale.set(scaleX * frame.scaleX, scaleY * frame.scaleY)
        this.target.rotation = rotation + frame.rotation
        this.target.position.set(ground.x + dx * cos - dy * sin + Math.round(frame.dx ?? 0), ground.y + dx * sin + dy * cos - Math.round(frame.lift))
    }

    /** Put the frame back as it was found, and stop. */
    reset(): void {
        this.playing = undefined
        this.restore()
        this.target = undefined
        this.base = undefined
    }

    private restore(): void {
        if (this.target && this.base && !this.target.destroyed) {
            this.target.position.set(this.base.x, this.base.y)
            this.target.scale.set(this.base.scaleX, this.base.scaleY)
            this.target.rotation = this.base.rotation
        }
    }
}
